import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "../components/Button";
import Layout from "../components/layouts/Layout";
import PasswordField from "../components/PasswordField";
import TextField from "../components/TextField";
import ROUTES from "../setup/routes";
import { DispatchContext } from "../store/context";
// Same swap as Login.tsx: logo.png was a dark-text wordmark that only
// ever looked right on a light card. Reuse the same light-logo.svg /
// darkLogo.svg pair the Sidebar already picks between for light vs.
// dark mode.
import ErrorBoundary from "../components/ErrorBoundary";
import { useFetchApi } from "../hooks/useFetchApi";
import Alert from "../components/Alert";
import AuthScreenLayout from "../components/authScreens/authScreenLayout";
import { Button as AntButton, Checkbox, Modal } from "antd";
import type { CheckboxProps } from "antd";
import { Tag } from "lucide-react";
import "../styles/theme-tokens.css";
import "./signup.theme.css";

export const _h = "18";

const Signup: React.FC = (): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [d, setD] = useState<any>({});
  const [agreement, setAgreement] = useState(true);
  const [cseChecked, setCseChecked] = useState(false);
  const [qseChecked, setQseChecked] = useState(false);
  const [sseChecked, setSseChecked] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showCoupon, setShowCoupon] = useState(false);
  const dispatch = useContext(DispatchContext);

  const MARKET_IDS = {
    CSE: 1,
    QSE: 6,
    SSE: 10,
  };

  const { execute, response, loading } = useFetchApi(
    "/api/register",
    {},
    "POST",
  );

  const handleBack = () => {
    navigate(ROUTES.LOGIN);
  };

  const handleChange = (e: any) => {
    const id = e.target.id;
    const value = e.target.value;
    setD({ ...d, [id]: value });
  };

  const updateMarketPreferences = (
    cseSelected: boolean,
    qseSelected: boolean,
    sseSelected: boolean,
  ) => {
    const marketPreferences: number[] = [];
    if (cseSelected) marketPreferences.push(MARKET_IDS.CSE);
    if (qseSelected) marketPreferences.push(MARKET_IDS.QSE);
    if (sseSelected) marketPreferences.push(MARKET_IDS.SSE);

    setD((prev: any) => ({ ...prev, marketPreferences }));
  };

  const onCseChange: CheckboxProps["onChange"] = (e) => {
    const isChecked = e.target.checked;
    setCseChecked(isChecked);
    updateMarketPreferences(isChecked, qseChecked, sseChecked);
  };

  const onQseChange: CheckboxProps["onChange"] = (e) => {
    const isChecked = e.target.checked;
    setQseChecked(isChecked);
    updateMarketPreferences(cseChecked, isChecked, sseChecked);
  };

  const onSseChange: CheckboxProps["onChange"] = (e) => {
    const isChecked = e.target.checked;
    setSseChecked(isChecked);
    updateMarketPreferences(cseChecked, qseChecked, isChecked);
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSignup = (e: any) => {
    e.preventDefault();
    console.log("d", d);
    if (d["password"] !== d["confirmPassword"]) {
      dispatch({
        type: "UPDATE_ALERT",
        payload: { type: "error", message: "Passwords don't match" },
      });

      return;
    }

    if (!d["email"] || !isValidEmail(d["email"])) {
      dispatch({
        type: "UPDATE_ALERT",
        payload: {
          type: "error",
          message: "Please enter a valid email address",
        },
      });
      return;
    }

    if (!cseChecked && !qseChecked && !sseChecked) {
      dispatch({
        type: "UPDATE_ALERT",
        payload: {
          type: "error",
          message: "Please select at least one market preference",
        },
      });
      return;
    }

    if (d["coupon_code"] !== "CFADOHA" && d["coupon_code"]) {
      dispatch({
        type: "UPDATE_ALERT",
        payload: {
          type: "error",
          message: "Invalid Coupon Code",
        },
      });
      return;
    }

    execute({ ...d });
  };

  const handleGoBack = () => {
    navigate(ROUTES.LOGIN);
  };

  useEffect(() => {
    console.log("re", response);
    if (response && response.status === "error") {
      dispatch({
        type: "UPDATE_ALERT",
        payload: { type: "error", message: response.message },
      });
    }
  }, [response]);

  const SignupActivationScreen = () => {
    return (
      <div className="mt-10 flex flex-col items-center">
        <h2 className="text-2xl font-semibold mb-4 font-dm text-center text-[color:var(--vi-accent)]">
          Activate Your Account
        </h2>
        <p className="mb-6 font-dm text-center text-[color:var(--vi-text-secondary)]">
          Thank you for signing up! To activate your account, please click the
          activation link sent to your email.
        </p>
        <Button onClick={handleGoBack} variant="primary">
          Back to Login
        </Button>
      </div>
    );
  };

  return (
    <Layout preLogin>
      <div className="vi-signup w-full">
        <AuthScreenLayout>
          <div className="vi-signup-pane w-full md:w-10/12 p-8 flex flex-col justify-center items-center relative">
            {/* Same decorative accent glows as Login.tsx — kept local to
                this page's own stylesheet rather than theme-tokens.css. */}
            <div className="vi-signup-glow vi-signup-glow-1" />
            <div className="vi-signup-glow vi-signup-glow-2" />

            <div className="vi-signup-card w-full max-w-xl relative">
              {response && response.status === "success" ? (
                <>
                  <SignupActivationScreen />

                  <ErrorBoundary />
                </>
              ) : (
                <>
                  <div className="vi-signup-badge">
                    <span className="vi-signup-badge-dot" />
                    Create your account
                  </div>
                  <h1 className="vi-signup-title text-[32px] mb-3 leading-tight">
                    Join Valueit today
                  </h1>
                  <p className="vi-signup-subtitle mb-6">
                    Fill in your details to start making smarter investment
                    decisions.
                  </p>
                  <div className="mt-6">
                    <div>
                      <Alert />
                      <form
                        onSubmit={handleSignup}
                        className="space-y-6 overflow-y-auto "
                      >
                        <div className="flex flex-col sm:flex-row gap-2">
                          <div className="flex-1">
                            <label
                              htmlFor="full_name"
                              className="vi-signup-label"
                            >
                              Name<span className="vi-signup-required">*</span>
                            </label>
                            <div className="mt-2">
                              <TextField
                                full
                                id="full_name"
                                type="text"
                                onChange={handleChange}
                                className="vi-signup-input rounded-[10px] font-dm h-[44px]"
                              />
                            </div>
                          </div>

                          <div className="flex-1">
                            <label htmlFor="email" className="vi-signup-label">
                              Email<span className="vi-signup-required">*</span>
                            </label>
                            <div className="mt-2">
                              <TextField
                                full
                                type="email"
                                id="email"
                                onChange={handleChange}
                                className="vi-signup-input rounded-[10px] font-dm h-[44px]"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2">
                          <div className="flex-1">
                            <label
                              htmlFor="password"
                              className="vi-signup-label"
                            >
                              Password
                              <span className="vi-signup-required">*</span>
                            </label>
                            <div className="mt-2">
                              <PasswordField
                                full
                                id="password"
                                onChange={handleChange}
                                className="vi-signup-input rounded-[10px] font-dm h-[44px]"
                              />
                            </div>
                          </div>

                          <div className="flex-1">
                            <label
                              htmlFor="confirmPassword"
                              className="vi-signup-label"
                            >
                              Confirm password
                              <span className="vi-signup-required">*</span>
                            </label>
                            <div className="mt-2">
                              <PasswordField
                                full
                                id="confirmPassword"
                                onChange={handleChange}
                                className="vi-signup-input rounded-[10px] font-dm h-[44px]"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="mt-4">
                          <label
                            htmlFor="marketPreferences"
                            className="vi-signup-label"
                          >
                            Market Preferences
                            <span className="vi-signup-required">*</span>
                          </label>
                          <div className="mt-2 ml-1">
                            <div className="flex gap-6">
                              <Checkbox
                                checked={cseChecked}
                                onChange={onCseChange}
                                className="font-dm"
                              >
                                CSE
                              </Checkbox>
                              <Checkbox
                                checked={qseChecked}
                                onChange={onQseChange}
                                className="font-dm"
                              >
                                QSE
                              </Checkbox>
                              <Checkbox
                                checked={sseChecked}
                                onChange={onSseChange}
                                className="font-dm"
                              >
                                SSE
                              </Checkbox>
                            </div>
                          </div>
                        </div>

                        {/* Coupon Code is optional — kept out of the way
                          behind an inviting toggle chip rather than an
                          always-visible field, so the form doesn't ask
                          for more than it needs up front. */}
                        <div>
                          {showCoupon ? (
                            <div className="max-w-xs">
                              <label
                                htmlFor="coupon_code"
                                className="vi-signup-label flex items-center justify-between"
                              >
                                <span className="flex items-center gap-1.5">
                                  <Tag size={14} />
                                  Coupon Code
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowCoupon(false);
                                    setD((prev: any) => {
                                      const { coupon_code, ...rest } = prev;
                                      return rest;
                                    });
                                  }}
                                  className="vi-signup-link-accent text-xs font-normal"
                                >
                                  Remove
                                </button>
                              </label>
                              <div className="mt-2">
                                <TextField
                                  full
                                  id="coupon_code"
                                  onChange={handleChange}
                                  placeholder="Enter coupon code"
                                  className="vi-signup-input rounded-[10px] font-dm h-[44px]"
                                />
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setShowCoupon(true)}
                              className="vi-signup-coupon-toggle"
                            >
                              <Tag size={16} />
                              Have a coupon code?
                            </button>
                          )}
                        </div>
                        <div className="flex items-center ml-1">
                          <input
                            id="remember-me"
                            name="remember-me"
                            type="checkbox"
                            className="h-4 w-4 rounded vi-signup-checkbox"
                            style={{ accentColor: "var(--vi-accent)" }}
                            onChange={(e) => setAgreement(!agreement)}
                          />
                          <label className="ml-3 block text-sm leading-6 vi-signup-muted font-dm">
                            I understand the{" "}
                            <button
                              type="button"
                              onClick={() => setShowTerms(true)}
                              className="vi-signup-link-accent underline"
                            >
                              Terms of Use
                            </button>
                          </label>
                        </div>

                        <Modal
                          title="Terms of Use Agreement"
                          open={showTerms}
                          onCancel={() => setShowTerms(false)}
                          footer={null}
                          wrapClassName="vi-signup-modal"
                          width={720}
                        >
                          <div className="vi-signup-terms font-dm">
                            <p>
                              This Terms of Use Agreement
                              (&ldquo;Agreement&rdquo;) governs your access to
                              and use of the Valueit website (the
                              &ldquo;Platform&rdquo;) operated by Valueit
                              (Private) Limited (&ldquo;we,&rdquo;
                              &ldquo;us,&rdquo; or &ldquo;our&rdquo;). This
                              Agreement is a binding contract between you
                              (&ldquo;you&rdquo; or &ldquo;User&rdquo;) and us.
                            </p>

                            <h3>Acceptance of Terms</h3>
                            <p>
                              By accessing or using the Platform, you
                              acknowledge that you have read, understood, and
                              agree to be bound by the terms of this Agreement.
                              If you do not agree to all the terms of this
                              Agreement, you are not authorized to use the
                              Platform.
                            </p>

                            <h3>Information Provided</h3>
                            <p>
                              The Platform provides access to financial
                              information, including stock quotes, news,
                              analysis, and other data
                              (&ldquo;Information&rdquo;) related to publicly
                              traded companies. We strive to provide accurate
                              and up-to-date Information, but we do not
                              guarantee its accuracy, completeness, or
                              timeliness. You acknowledge that the Information
                              is for informational purposes only and should not
                              be considered as investment advice or a
                              recommendation to buy or sell any security.
                            </p>

                            <h3>Disclaimer of Warranties</h3>
                            <p>
                              THE INFORMATION AND THE PLATFORM ARE PROVIDED
                              &ldquo;AS IS&rdquo; AND WITHOUT WARRANTIES OF ANY
                              KIND, EXPRESS OR IMPLIED. WE DISCLAIM ALL
                              WARRANTIES, INCLUDING, BUT NOT LIMITED TO,
                              WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                              PARTICULAR PURPOSE, NON-INFRINGEMENT, AND
                              ACCURACY. WE DO NOT WARRANT THAT THE PLATFORM WILL
                              BE UNINTERRUPTED OR ERROR-FREE, THAT DEFECTS WILL
                              BE CORRECTED, OR THAT THE PLATFORM OR THE SERVER
                              THAT MAKES IT AVAILABLE ARE FREE OF VIRUSES OR
                              OTHER HARMFUL COMPONENTS.
                            </p>

                            <h3>Limitation of Liability</h3>
                            <p>
                              WE WILL NOT BE LIABLE FOR ANY DIRECT, INDIRECT,
                              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
                              DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF
                              THE PLATFORM OR THE INFORMATION, INCLUDING, BUT
                              NOT LIMITED TO, LOST PROFITS, TRADING LOSSES,
                              DAMAGE TO PROPERTY, OR PERSONAL INJURY.
                            </p>

                            <h3>User Conduct</h3>
                            <p>
                              You agree to use the Platform in a lawful and
                              responsible manner. You will not:
                            </p>
                            <ul>
                              <li>
                                Use the Platform for any illegal or unauthorized
                                purpose;
                              </li>
                              <li>
                                Interfere with or disrupt the Platform or
                                servers or networks connected to the Platform;
                              </li>
                              <li>
                                Transmit any viruses or other harmful code
                                through the Platform;
                              </li>
                              <li>
                                Infringe on our intellectual property rights or
                                the intellectual property rights of others;
                              </li>
                              <li>
                                Collect or store personal data about other users
                                without their consent; or
                              </li>
                              <li>Impersonate any person or entity.</li>
                            </ul>

                            <h3>Intellectual Property</h3>
                            <p>
                              The Platform and all of the content on the
                              Platform, including, but not limited to, text,
                              graphics, logos, images, and software, are the
                              property of us or our licensors and are protected
                              by copyright, trademark, and other intellectual
                              property laws. You may not reproduce, modify,
                              distribute, display, or create derivative works
                              from any content on the Platform without our
                              express written permission.
                            </p>

                            <h3>Termination</h3>
                            <p>
                              We may terminate your access to the Platform at
                              any time and for any reason, with or without
                              notice.
                            </p>

                            <h3>Governing Law and Jurisdiction</h3>
                            <p>
                              This Agreement shall be governed by and construed
                              in accordance with the laws of England and Wales,
                              without regard to its conflict of laws provisions.
                              You agree that any legal action or proceeding
                              arising out of or relating to this Agreement shall
                              be brought exclusively in the courts of England
                              and Wales, and you hereby consent to the personal
                              jurisdiction and venue of such courts.
                            </p>

                            <h3>Entire Agreement</h3>
                            <p>
                              This Agreement constitutes the entire agreement
                              between you and us with respect to your use of the
                              Platform and supersedes all prior or
                              contemporaneous communications, representations,
                              or agreements, whether oral or written.
                            </p>

                            <h3>Severability</h3>
                            <p>
                              If any provision of this Agreement is held to be
                              invalid or unenforceable, such provision shall be
                              struck and the remaining provisions shall remain
                              in full force and effect.
                            </p>

                            <h3>Amendments</h3>
                            <p>
                              We may amend this Agreement at any time by posting
                              the amended terms on the Platform. Your continued
                              use of the Platform after the amended terms are
                              posted will constitute your acceptance of the
                              amended Agreement.
                            </p>

                            <h3>Contact Us</h3>
                            <p>
                              If you have any questions about this Agreement,
                              please contact us at{" "}
                              <a href="mailto:info@valueit.io">
                                info@valueit.io
                              </a>
                            </p>
                          </div>
                        </Modal>
                        <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
                          <AntButton
                            type="primary"
                            htmlType="submit"
                            size="large"
                            disabled={agreement}
                            loading={loading}
                            onClick={handleSignup}
                            className="vi-signup-submit w-full sm:w-auto sm:min-w-[150px] font-dm text-base font-medium"
                            style={{
                              background: "var(--vi-accent-grad)",
                              borderRadius: "10px",
                              color: "var(--vi-accent-on)",
                              border: "none",
                            }}
                            styles={{
                              icon: {
                                color: "var(--vi-accent-on)",
                              },
                            }}
                          >
                            {loading
                              ? "Creating your account..."
                              : t("signup.text.createNewAccountBtn")}
                          </AntButton>
                          <AntButton
                            type="primary"
                            size="large"
                            disabled={loading}
                            onClick={handleBack}
                            className="vi-signup-submit vi-signup-btn-glass vi-signup-btn-secondary-glow w-full sm:w-auto sm:min-w-[150px] font-dm text-base font-medium"
                            style={{
                              background:
                                "color-mix(in srgb, var(--vi-accent-2) 10%, transparent)",
                              border: "1px solid var(--vi-accent-2)",
                              borderRadius: "10px",
                              color: "var(--vi-accent-2)",
                            }}
                          >
                            {t("signup.text.goBackToLoginBtn")}
                          </AntButton>
                        </div>
                      </form>
                    </div>
                  </div>
                </>
              )}

              <p className="text-center text-sm vi-signup-muted mt-8 font-dm">
                ©2026 Valueit. All rights reserved.
              </p>
            </div>
          </div>
        </AuthScreenLayout>
      </div>
    </Layout>
  );
};

export default Signup;
